<template>
    <div class="msp-grafico-lap">
        <div class="btns_grafico">
            <div
                v-for="zone in availableZones"
                :key="zone"
                class="btn_misuraGraficoLap"
                :class="{ active: activeZone === zone }"
                @click="activeZone = zone"
            >{{ zone }}</div>
        </div>
        <div v-if="displayIndex !== null && hoveredLapRows.length" class="lap-info">
            <div v-for="row in hoveredLapRows" :key="row.key" class="lap-info__row" :class="'lap-info__row--'+row.key">
                <span class="lap-info__label">{{ row.label }}</span>
                <span class="lap-info__value">{{ row.value }}<span v-if="row.suffix"> {{ row.suffix }}</span></span>
            </div>
            <div class="lap-info__nav">
                <button class="lap-info__nav-btn" :disabled="displayIndex <= 0" @click="pinnedIndex = displayIndex - 1"><i class="msp-icon-left"></i></button>
                <button class="lap-info__nav-btn" :disabled="displayIndex >= laps.length - 1" @click="pinnedIndex = displayIndex + 1"><i class="msp-icon-right"></i></button>
            </div>
        </div>

        <div style="position:relative; height:200px;">
            <canvas ref="canvas"></canvas>
        </div>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const variwidePlugin = {
    id: 'variwidePlugin',
    beforeDatasetsDraw(chart) {
        const { x: xScale, y: yScale } = chart.scales;
        const dataset = chart.data.datasets[0];
        if (!dataset || !dataset.lapData) return;
        const meta = chart.getDatasetMeta(0);
        const gap = 2;
        dataset.lapData.forEach((lap, i) => {
            const el = meta.data[i];
            if (!el) return;
            const xStart = xScale.getPixelForValue(lap.xStart);
            const xEnd   = xScale.getPixelForValue(lap.xEnd);
            const width  = Math.max(1, xEnd - xStart - gap);
            el.x     = xStart + gap / 2 + width / 2;
            el.width = width;
            el.base  = yScale.getPixelForValue(0);
        });
    },
    afterDatasetsDraw(chart) {
        const dataset = chart.data.datasets[0];
        if (!dataset || !dataset.lapData) return;
        const hoveredIndex = dataset.hoveredIndex;
        if (hoveredIndex == null) return;
        const meta = chart.getDatasetMeta(0);
        const { ctx } = chart;
        ctx.save();
        // overlay semitrasparente sui lap non selezionati
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        dataset.lapData.forEach((_, i) => {
            if (i === hoveredIndex) return;
            const el = meta.data[i];
            if (el) ctx.fillRect(el.x - el.width / 2, el.y, el.width, el.base - el.y);
        });
        // bordo sul lap evidenziato
        const highlighted = meta.data[hoveredIndex];
        if (highlighted) {
            ctx.strokeStyle = 'rgba(0,0,0,0.8)';
            ctx.lineWidth = 1;
            ctx.strokeRect(
                highlighted.x - highlighted.width / 2,
                highlighted.y,
                highlighted.width,
                highlighted.base - highlighted.y
            );
        }
        ctx.restore();
    },
};

const ORDER_LABEL = {
    ciclismo: ['lap','inizio','zona','tempo','distanza_totale','velocita_avg','cadenza_avg','cardio_avg','watt_kg_avg','potenza_avg','potenza_norm','potenza_max'],
    podismo:  ['lap','inizio','zona','tempo','distanza_totale','cadenza_avg','cardio_avg','cardio_max','watt_kg_avg','passo_km_avg','passo_km_norm','potenza_avg'],
    nuoto:    ['lap','inizio','zona','tempo','distanza_totale','cadenza_avg','cardio_avg','cardio_max','watt_kg_avg','passo_100mt_avg','passo_100mt_norm'],
};

export default {
    name: 'mspGraficoLap',

    props: {
        laps:        { type: Array,  default: null },
        fields:      { type: Array,  default: null },
        durata:      { type: Number, default: 0 },
        sport_codice:{ type: String, default: null },
    },

    data() {
        return {
            activeZone:   null,
            hoveredIndex: null,
            pinnedIndex:  0,
        };
    },

    computed: {
        displayIndex() {
            return this.hoveredIndex !== null ? this.hoveredIndex : this.pinnedIndex;
        },

        availableZones() {
            if (!this.laps) return [];
            const zones = new Set();
            this.laps.forEach(lap => {
                Object.entries(lap.zone || {}).forEach(([key, z]) => {
                    if (z && z[1]) zones.add(key);
                });
            });
            return [...zones];
        },

        mergedLaps() {
            if (!this.laps || !this.activeZone) return [];
            return this.laps.map(lap => {
                const zona = lap.zone && lap.zone[this.activeZone];
                return {
                    xStart: lap.x,
                    xEnd:   lap.x + lap.z,
                    y:      zona ? zona[1] : 0,
                    color:  zona ? zona[2] : '#ccc',
                };
            });
        },

        fieldsMeta() {
            if (!this.fields) return {};
            return Object.fromEntries(this.fields.map(f => [f.key, f]));
        },

        hoveredLapRows() {
            if (this.displayIndex == null || !this.laps) return [];
            const lap = this.laps[this.displayIndex];
            if (!lap || !lap.v) return [];
            const zona = lap.zone && lap.zone[this.activeZone];
            const v = { ...lap.v };
            const vs = lap.vs || {};
            const order = ORDER_LABEL[this.sport_codice] || Object.keys(v);
            return order
                .filter(key => key === 'zona' ? true : v[key] != null)
                .map(key => {
                    if (key === 'zona') {
                        return { key, label: `Zona ${this.activeZone}`, value: zona ? zona[0] : '-', suffix: '' };
                    }
                    const meta = this.fieldsMeta[key] || {};
                    let label;
                    if (meta.label_short) {
                        label = meta.label_short;
                    } else {
                        const suffixes = (meta.label_suffixes || []).filter(Boolean);
                        label = [meta.label || key, ...suffixes].join(' ');
                    }
                    const suffix = vs[key] !== undefined ? vs[key] : (meta.value_suffix || '');
                    return {
                        key,
                        label,
                        value:  v[key] ?? '-',
                        suffix,
                    };
                });
        },
    },

    watch: {
        laps() {
            this.$nextTick(() => this._initChart());
        },
        availableZones(zones) {
            if (zones.length && !this.activeZone) this.activeZone = zones[0];
        },
        activeZone() {
            this._updateChartData();
        },
        hoveredIndex(val) {
            if (val !== null) this.pinnedIndex = val;
        },

        displayIndex(index) {
            if (this._chart) {
                this._chart.data.datasets[0].hoveredIndex = index;
                this._chart.update('none');
            }
        },
    },

    mounted() {
        this._initChart();
    },

    beforeDestroy() {
        this._destroyChart();
    },

    methods: {
        _initChart() {
            this._destroyChart();
            if (!this.laps || !this.laps.length) return;
            if (!this.activeZone && this.availableZones.length) {
                this.activeZone = this.availableZones[0];
            }
            const canvas = this.$refs.canvas;
            if (!canvas) return;
            this._canvas = canvas;

            this._chart = new Chart(canvas, {
                type: 'bar',
                data: {
                    datasets: [{
                        lapData:       [],
                        hoveredIndex:  null,
                        data:          [],
                        backgroundColor: [],
                        borderColor:   '#ffffff',
                        borderWidth:   1,
                        borderRadius:  0,
                        barPercentage: 1.0,
                        categoryPercentage: 1.0,
                    }],
                },
                options: {
                    animation: false,
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend:  { display: false },
                        tooltip: { enabled: false },
                    },
                    scales: {
                        x: {
                            type: 'linear',
                            offset: false,
                            min: 0,
                            max: this.durata * 1000,
                            grid: { display: false },
                            ticks: {
                                maxTicksLimit: 6,
                                callback: (val) => {
                                    const s = Math.floor(val / 1000);
                                    const h = Math.floor(s / 3600);
                                    const m = Math.floor((s % 3600) / 60);
                                    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
                                },
                            },
                        },
                        y: {
                            min: 0,
                            grid: { display: false },
                            ticks: { display: false },
                        },
                    },
                },
                plugins: [variwidePlugin],
            });

            const getIdxFromClientX = (clientX) => {
                if (!this._chart) return -1;
                const rect = canvas.getBoundingClientRect();
                const mouseX = clientX - rect.left;
                const xScale = this._chart.scales.x;
                const lapData = this._chart.data.datasets[0].lapData;
                if (!lapData) return -1;
                return lapData.findIndex(lap => {
                    const x0 = xScale.getPixelForValue(lap.xStart);
                    const x1 = xScale.getPixelForValue(lap.xEnd);
                    return mouseX >= x0 && mouseX < x1;
                });
            };

            this._onMouseMove = (e) => {
                const idx = getIdxFromClientX(e.clientX);
                this.hoveredIndex = idx === -1 ? null : idx;
            };
            this._onMouseLeave = () => { this.hoveredIndex = null; };

            let _touchStartX = 0, _touchStartY = 0;
            this._onTouchStart = (e) => {
                _touchStartX = e.touches[0].clientX;
                _touchStartY = e.touches[0].clientY;
            };
            this._onTouchEnd = (e) => {
                const t = e.changedTouches[0];
                if (Math.abs(t.clientX - _touchStartX) > 10 || Math.abs(t.clientY - _touchStartY) > 10) return;
                const idx = getIdxFromClientX(t.clientX);
                if (idx !== -1) this.pinnedIndex = idx;
            };

            canvas.addEventListener('mousemove',  this._onMouseMove);
            canvas.addEventListener('mouseleave', this._onMouseLeave);
            canvas.addEventListener('touchstart', this._onTouchStart, { passive: true });
            canvas.addEventListener('touchend',   this._onTouchEnd,   { passive: true });

            this._updateChartData();
        },

        _updateChartData() {
            if (!this._chart || !this.mergedLaps.length) return;
            const lapData = this.mergedLaps;
            const dataset = this._chart.data.datasets[0];
            dataset.lapData = lapData;
            dataset.data = lapData.map(lap => ({ x: (lap.xStart + lap.xEnd) / 2, y: lap.y }));
            dataset.backgroundColor = lapData.map(lap => lap.color);
            dataset.hoveredIndex = this.hoveredIndex;
            const values = lapData.map(l => l.y).filter(Boolean);
            if (values.length) this._chart.options.scales.y.max = Math.max(...values) * 1.15;
            this._chart.update('none');
        },

        _destroyChart() {
            if (this._canvas) {
                if (this._onMouseMove)  this._canvas.removeEventListener('mousemove',  this._onMouseMove);
                if (this._onMouseLeave) this._canvas.removeEventListener('mouseleave', this._onMouseLeave);
                if (this._onTouchStart) this._canvas.removeEventListener('touchstart', this._onTouchStart);
                if (this._onTouchEnd)   this._canvas.removeEventListener('touchend',   this._onTouchEnd);
            }
            if (this._chart) {
                this._chart.destroy();
                this._chart = null;
            }
        },
    },
};
</script>

<style lang="scss">
.msp-grafico-lap {
    width: 100%;

    .btns_grafico {
        display: flex;
        gap: 5px;
        padding: 3px;
    }

    .btn_misuraGraficoLap {
        padding: 5px;
        border-radius: 3px;
        border: 1px solid var(--col-grigio-chiaro, #ccc);
        cursor: pointer;

        &.active {
            border-color: var(--col-msp, #007bff);
            background-color: var(--col-msp-chiaro, #e8f0fe);
        }
    }

    .lap-info {
        display: grid;
        grid-template-columns: repeat(3,1fr);
        gap: 2px 8px;
        padding: 6px 4px;
        font-size: 0.85em;

        &__nav {
            grid-column: 1 / -1;
            display: flex;
            margin-bottom: 4px;
        }

        &__nav-btn {
            flex: 1;
            background: none;
            border: 1px solid var(--col-grigio-chiaro, #ccc);
            border-radius: 0;
            padding: 6px 0;
            font-size: 1.2em;
            cursor: pointer;
            line-height: 1;
            &:active {
                background: var(--col-msp-chiarissimo);
            }
            i {
            display: flex;
            align-items: center;
            justify-content: center;
        }
            &:first-child { border-radius: 3px 0 0 3px; }
            &:last-child  { border-radius: 0 3px 3px 0; }

            & + & { border-left: none; }

            &.--close {
                flex: 0 0 auto;
                padding: 6px 12px;
                color: #888;
            }

            &:disabled {
                opacity: 0.3;
                cursor: default;
            }
        }

        &__row {
            display: flex;
            flex-direction: column;
        }

        &__label {
            color: #888;
            line-height: 1;
        }

        &__value {
            font-weight: bold;
            font-size: 1.3em;
            margin-bottom: var(--spacing-2);
        }

    }
}
</style>
